import { FormEvent, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRightLeft,
  Bell,
  Check,
  ChevronRight,
  CircleStop,
  Code2,
  FileCode2,
  FolderGit2,
  ListFilter,
  ListTree,
  LoaderCircle,
  LockKeyhole,
  LogOut,
  MessageSquareText,
  MonitorCog,
  Plus,
  RefreshCw,
  Send,
  Server,
  ShieldCheck,
  Star,
  Tag,
  TerminalSquare,
  X,
} from "lucide-react";
import { api } from "./api";
import type { Approval, CodexThread, Executor, LiveMessage, SessionState, ThreadItem } from "./types";
import { itemText, relativeTime, shortPath, statusKind, statusLabel, threadTitle, type StatusKind } from "./utils";

type Filter = "all" | "favorites" | "waiting" | "running" | "done";

export default function App() {
  const [session, setSession] = useState<SessionState | null>(null);
  const [threads, setThreads] = useState<CodexThread[]>([]);
  const [executors, setExecutors] = useState<Executor[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(() => new URLSearchParams(location.search).get("thread"));
  const [selected, setSelected] = useState<CodexThread | null>(null);
  const [approvals, setApprovals] = useState<Approval[]>([]);
  const [activeApproval, setActiveApproval] = useState<Approval | null>(null);
  const [filter, setFilter] = useState<Filter>("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [showNewTask, setShowNewTask] = useState(false);
  const refreshTimer = useRef<number | null>(null);
  const selectedIdRef = useRef(selectedId);
  const detailRequest = useRef<{ sequence: number; controller: AbortController | null }>({ sequence: 0, controller: null });

  const navigateToThread = useCallback((threadId: string | null, replace = false) => {
    const url = new URL(location.href);
    if (threadId) url.searchParams.set("thread", threadId);
    else url.searchParams.delete("thread");
    url.searchParams.delete("approval");
    history[replace ? "replaceState" : "pushState"]({ threadId }, "", url);
    selectedIdRef.current = threadId;
    setSelectedId(threadId);
    if (!threadId) setSelected(null);
  }, []);

  const refreshThreads = useCallback(async () => {
    const payload = await api<{ data: CodexThread[] }>("/api/threads");
    setThreads(payload.data);
  }, []);

  const refreshApprovals = useCallback(async () => {
    const payload = await api<{ data: Approval[] }>("/api/approvals");
    setApprovals(payload.data);
  }, []);

  const refreshExecutors = useCallback(async () => {
    const payload = await api<{ data: Executor[] }>("/api/executors");
    setExecutors(payload.data);
  }, []);

  const refreshSelected = useCallback(async (threadId = selectedIdRef.current, fresh = false) => {
    if (!threadId) return;
    detailRequest.current.controller?.abort();
    const controller = new AbortController();
    const sequence = detailRequest.current.sequence + 1;
    detailRequest.current = { sequence, controller };
    try {
      const suffix = fresh ? "?fresh=1" : "";
      const payload = await api<{ thread: CodexThread }>(`/api/threads/${encodeURIComponent(threadId)}${suffix}`, { signal: controller.signal });
      if (detailRequest.current.sequence === sequence && selectedIdRef.current === threadId) setSelected(payload.thread);
    } catch (caught) {
      if (!(caught instanceof DOMException && caught.name === "AbortError")) throw caught;
    }
  }, []);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([refreshThreads(), refreshApprovals(), refreshExecutors()]);
      if (selectedIdRef.current) await refreshSelected();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "刷新失败");
    } finally {
      setLoading(false);
    }
  }, [refreshApprovals, refreshExecutors, refreshSelected, refreshThreads]);

  useEffect(() => {
    void api<SessionState>("/api/session").then(setSession).catch(() => setSession({ authenticated: false }));
  }, []);

  useEffect(() => {
    const onPopState = () => {
      const threadId = new URLSearchParams(location.search).get("thread");
      selectedIdRef.current = threadId;
      setSelectedId(threadId);
      if (!threadId) setSelected(null);
    };
    addEventListener("popstate", onPopState);
    return () => removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    if (!session?.authenticated) return;
    void refreshAll();
    const protocol = location.protocol === "https:" ? "wss:" : "ws:";
    let socket: WebSocket | null = null;
    let reconnect: number | null = null;
    let disposed = false;

    const connect = () => {
      if (disposed) return;
      socket = new WebSocket(`${protocol}//${location.host}/live`);
      socket.onmessage = (event) => {
        const message = JSON.parse(event.data) as LiveMessage;
        if (message.type === "snapshot") {
          setSession((current) => (current ? { ...current, codex: message.codex } : current));
          setApprovals(message.approvals ?? []);
          if (message.executors) setExecutors(message.executors);
          const requestedApproval = new URLSearchParams(location.search).get("approval");
          if (requestedApproval) {
            const match = message.approvals?.find((approval) => approval.key === requestedApproval);
            if (match) setActiveApproval(match);
          }
          return;
        }
        if (message.type === "bridge-state") {
          setSession((current) => (current ? { ...current, codex: message.state } : current));
          return;
        }
        if (message.type === "approval-added" && message.approval && "method" in message.approval) {
          const approval = message.approval as Approval;
          setApprovals((current) => [approval, ...current.filter((item) => item.key !== approval.key)]);
          showNotification("Codex 等待审批", approval.params.command?.toString() || approval.params.reason?.toString() || approval.method);
        }
        if (message.type === "approval-resolved" && message.approval) {
          setApprovals((current) => current.filter((item) => item.key !== message.approval!.key));
          setActiveApproval((current) => (current?.key === message.approval!.key ? null : current));
        }
        if (message.type === "codex-notification") {
          const method = message.message?.method;
          if (method === "turn/started" || method === "turn/completed") {
            if (refreshTimer.current) window.clearTimeout(refreshTimer.current);
            refreshTimer.current = window.setTimeout(() => {
              void refreshThreads();
              if (method === "turn/completed" && selectedIdRef.current) void refreshSelected(undefined, true);
            }, 300);
          }
        }
        if (message.type === "executor-changed") {
          if (refreshTimer.current) window.clearTimeout(refreshTimer.current);
          refreshTimer.current = window.setTimeout(() => void refreshExecutors(), 200);
        }
        if (message.type === "task-transferred") {
          if (refreshTimer.current) window.clearTimeout(refreshTimer.current);
          refreshTimer.current = window.setTimeout(() => {
            void refreshThreads();
            if (selectedIdRef.current) void refreshSelected();
          }, 200);
        }
        if (message.type === "task-failover-changed") {
          if (refreshTimer.current) window.clearTimeout(refreshTimer.current);
          refreshTimer.current = window.setTimeout(() => {
            void refreshThreads();
            void refreshExecutors();
            if (selectedIdRef.current) void refreshSelected(undefined, true);
          }, 160);
        }
        if (message.type === "task-metadata-changed" && message.thread) {
          const metadata = message.thread;
          setThreads((current) => current.map((thread) => thread.id === metadata.id ? { ...thread, ...metadata } : thread));
          setSelected((current) => current?.id === metadata.id ? {
            ...current,
            favorite: metadata.favorite,
            category: metadata.category,
            updatedAt: metadata.updatedAt,
          } : current);
        }
        if ((message.type === "workflow-created" || message.type === "workflow-module-created" || message.type === "workflow-changed") && message.thread) {
          if (refreshTimer.current) window.clearTimeout(refreshTimer.current);
          refreshTimer.current = window.setTimeout(() => {
            void refreshThreads();
            if (selectedIdRef.current) void refreshSelected(undefined, true);
          }, 180);
        }
      };
      socket.onclose = () => {
        if (!disposed) reconnect = window.setTimeout(connect, 2_000);
      };
    };
    connect();
    return () => {
      disposed = true;
      if (reconnect) window.clearTimeout(reconnect);
      if (socket) socket.onclose = null;
      socket?.close();
    };
  }, [refreshAll, refreshExecutors, refreshSelected, refreshThreads, session?.authenticated]);

  useEffect(() => {
    if (!session?.authenticated || !selectedId) return;
    setSelected(null);
    selectedIdRef.current = selectedId;
    void refreshSelected(selectedId, true).catch((caught) => setError(caught instanceof Error ? caught.message : "加载失败"));
  }, [refreshSelected, selectedId, session?.authenticated]);

  useEffect(() => () => detailRequest.current.controller?.abort(), []);

  const categories = useMemo(() => Array.from(new Set(threads
    .map((thread) => thread.category?.trim())
    .filter((category): category is string => Boolean(category))))
    .sort((left, right) => left.localeCompare(right, "zh-CN")), [threads]);

  const filteredThreads = useMemo(() => threads.filter((thread) => {
    const matchesState = filter === "all"
      || (filter === "favorites" ? thread.favorite === true : statusKind(thread.status) === filter);
    const matchesCategory = categoryFilter === "all"
      || (categoryFilter === "uncategorized" ? !thread.category : thread.category === categoryFilter);
    return matchesState && matchesCategory;
  }), [categoryFilter, filter, threads]);

  const handleNotificationClick = async () => {
    try {
      await enablePushNotifications();
      const result = await api<{ sent: number }>("/api/push/test", { method: "POST" });
      setNotice(result.sent > 0 ? "测试通知已发送到手机" : "通知已开启，等待推送服务确认");
      window.setTimeout(() => setNotice(null), 4_000);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "开启手机通知失败");
    }
  };

  if (!session) return <FullScreenLoader />;
  if (!session.authenticated) return <Login onAuthenticated={setSession} />;

  return (
    <div className="app-shell">
      <header className="topbar">
        <div className="brand">
          <span className="brand-mark"><TerminalSquare size={20} /></span>
          <span>Codex Console</span>
        </div>
        <div className="topbar-actions">
          <ConnectionBadge state={session.codex} executors={executors} />
          <button className="icon-button" title="开启手机通知" onClick={() => void handleNotificationClick()}>
            <Bell size={19} />
          </button>
          <button className="icon-button notification-button" title="待审批" onClick={() => setActiveApproval(approvals[0] ?? null)}>
            <ShieldCheck size={19} />
            {approvals.length > 0 && <span className="notification-count">{approvals.length}</span>}
          </button>
          <button className="icon-button" title="刷新" onClick={() => void refreshAll()} disabled={loading}>
            <RefreshCw size={19} className={loading ? "spin" : ""} />
          </button>
          <button className="icon-button" title="退出" onClick={() => void logout(setSession)}>
            <LogOut size={19} />
          </button>
        </div>
      </header>

      {error && (
        <div className="error-banner">
          <AlertTriangle size={17} />
          <span>{error}</span>
          <button className="icon-button compact" title="关闭" onClick={() => setError(null)}><X size={16} /></button>
        </div>
      )}
      {notice && <div className="notice-banner"><Bell size={17} /><span>{notice}</span></div>}

      <main className={`workspace ${selectedId ? "has-selection" : ""}`}>
        <aside className="thread-pane">
          <div className="pane-heading">
            <div className="pane-heading-copy">
              <span className="pane-eyebrow">CODEX WORKSPACE</span>
              <div className="pane-title-line">
                <h1>任务总览</h1>
                <span className="muted-count">{threads.length}</span>
              </div>
            </div>
            <button className="primary-icon-button" title="新建任务" onClick={() => setShowNewTask(true)}><Plus size={20} /></button>
          </div>

          <div className="segmented" aria-label="筛选任务">
            {(["all", "favorites", "waiting", "running", "done"] as Filter[]).map((value) => (
              <button key={value} className={filter === value ? "active" : ""} onClick={() => setFilter(value)}>
                {filterLabel(value)}
              </button>
            ))}
          </div>

          <label className="category-filter">
            <Tag size={15} />
            <select aria-label="项目分类筛选" value={categoryFilter} onChange={(event) => setCategoryFilter(event.target.value)}>
              <option value="all">全部分类</option>
              <option value="uncategorized">未分类</option>
              {categories.map((category) => <option key={category} value={category}>{category}</option>)}
            </select>
          </label>

          <div className="thread-list">
            {filteredThreads.map((thread) => (
              <ThreadRow key={thread.id} thread={thread} selected={selectedId === thread.id} onClick={() => navigateToThread(thread.id)} />
            ))}
            {!loading && filteredThreads.length === 0 && (
              <div className="empty-list"><ListFilter size={25} /><span>没有匹配的任务</span></div>
            )}
          </div>
        </aside>

        <section className="detail-pane">
          {selected ? (
              <ThreadDetail
                thread={selected}
                workflowTasks={selected.workflow ? threads.filter((item) => item.workflow?.id === selected.workflow?.id) : []}
                onBack={() => navigateToThread(null)}
              onRefresh={() => {
                void refreshSelected(undefined, true);
                void refreshThreads();
              }}
              executors={executors}
              categories={categories}
              onTransferred={(threadId, executorName) => {
                navigateToThread(threadId, true);
                setNotice(`任务已交接到 ${executorName}`);
                window.setTimeout(() => setNotice(null), 5_000);
                void refreshAll();
              }}
              onThreadChanged={(threadId) => {
                setSelected(null);
                navigateToThread(threadId, true);
                setNotice("桌面任务正在使用，已创建远程分支");
                window.setTimeout(() => setNotice(null), 5_000);
                void refreshThreads();
              }}
              onError={setError}
                onMetadataChanged={(metadata) => {
                setThreads((current) => current.map((thread) => thread.id === metadata.id ? { ...thread, ...metadata } : thread));
                setSelected((current) => current?.id === metadata.id ? {
                  ...current,
                  favorite: metadata.favorite,
                  category: metadata.category,
                  updatedAt: metadata.updatedAt,
                } : current);
                }}
                onWorkflowChanged={() => void refreshThreads()}
                onSelectThread={(threadId) => navigateToThread(threadId)}
              />
          ) : selectedId ? <FullScreenLoader compact /> : <NoSelection onNew={() => setShowNewTask(true)} />}
        </section>
      </main>

      {approvals.length > 0 && (
        <button className="approval-dock" onClick={() => setActiveApproval(approvals[0])}>
          <ShieldCheck size={20} />
          <span>{approvals.length} 项待处理</span>
          <ChevronRight size={18} />
        </button>
      )}

      {activeApproval && (
        <ApprovalDialog
          approval={activeApproval}
          onClose={() => setActiveApproval(null)}
          onResolved={() => {
            setApprovals((current) => current.filter((item) => item.key !== activeApproval.key));
            setActiveApproval(null);
            void refreshAll();
          }}
          onError={setError}
        />
      )}
      {showNewTask && (
        <NewTaskDialog
          initialCwd={selected?.cwd || threads[0]?.cwd || ""}
          executors={executors}
          onClose={() => setShowNewTask(false)}
          onCreated={(threadId) => {
            setShowNewTask(false);
            navigateToThread(threadId);
            void refreshThreads();
          }}
          onError={setError}
        />
      )}
    </div>
  );
}

function Login({ onAuthenticated }: { onAuthenticated: (session: SessionState) => void }) {
  const [token, setToken] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api("/api/login", { method: "POST", body: JSON.stringify({ token }) });
      onAuthenticated(await api<SessionState>("/api/session"));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "登录失败");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="login-screen">
      <form className="login-panel" onSubmit={submit}>
        <div className="login-brand"><span className="brand-mark large"><TerminalSquare size={25} /></span><h1>Codex Console</h1></div>
        <label htmlFor="token">访问令牌</label>
        <div className="token-input"><LockKeyhole size={18} /><input id="token" type="password" autoComplete="current-password" value={token} onChange={(event) => setToken(event.target.value)} autoFocus /></div>
        {error && <p className="form-error">{error}</p>}
        <button className="primary-button" disabled={loading || !token}>{loading ? <LoaderCircle size={18} className="spin" /> : <ShieldCheck size={18} />}登录</button>
      </form>
    </main>
  );
}

function ThreadRow({ thread, selected, onClick }: { thread: CodexThread; selected: boolean; onClick: () => void }) {
  const kind = statusKind(thread.status);
  const workflowPrefix = thread.workflow?.role === "controller" ? "[主控] " : thread.workflow?.role === "module" ? "[模块] " : "";
  return (
    <button className={`thread-row ${selected ? "selected" : ""} ${thread.workflow?.role === "module" ? "workflow-module-row" : thread.workflow?.role === "controller" ? "workflow-controller-row" : ""}`} onClick={onClick}>
      <span className={`status-dot ${kind}`} />
      <span className="thread-summary">
        <strong>{thread.favorite && <Star className="favorite-star" size={13} fill="currentColor" />}{workflowPrefix}{threadTitle(thread)}</strong>
        <span><FolderGit2 size={13} />{shortPath(thread.cwd)}</span>
        {thread.category && <span className="thread-category"><Tag size={12} />{thread.category}</span>}
        {thread.checkpoint && <span className="thread-checkpoint"><RefreshCw size={12} />检查点 {relativeTime(thread.checkpoint.createdAt)}</span>}
      </span>
      <span className="thread-meta"><small>{relativeTime(thread.updatedAt)}</small><StatusText kind={kind} label={statusLabel(thread.status)} /></span>
    </button>
  );
}

function ThreadDetail({ thread, executors, categories, workflowTasks, onBack, onRefresh, onThreadChanged, onTransferred, onMetadataChanged, onWorkflowChanged, onSelectThread, onError }: { thread: CodexThread; executors: Executor[]; categories: string[]; workflowTasks: CodexThread[]; onBack: () => void; onRefresh: () => void; onThreadChanged: (threadId: string) => void; onTransferred: (threadId: string, executorName: string) => void; onMetadataChanged: (thread: CodexThread) => void; onWorkflowChanged: () => void; onSelectThread: (threadId: string) => void; onError: (value: string) => void }) {
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [resolvingConflict, setResolvingConflict] = useState(false);
  const [metadataSaving, setMetadataSaving] = useState(false);
  const [categoryDraft, setCategoryDraft] = useState(thread.category ?? "");
  const [showModuleDialog, setShowModuleDialog] = useState(false);
  const targets = executors.filter((executor) => executor.status === "connected" && executor.id !== thread.executorId);
  const [targetExecutorId, setTargetExecutorId] = useState("");
  const timelineRef = useRef<HTMLDivElement>(null);
  const activeTurn = [...thread.turns].reverse().find((turn) => turn.status === "inProgress" || turn.status === "running");
  const queued = thread.status.type === "active" && thread.status.activeFlags.includes("waitingInQueue");
  const waitingFailover = thread.status.type === "active" && thread.status.activeFlags.includes("waitingForFailover");
  const recoveringOnServer = thread.status.type === "active" && thread.status.activeFlags.includes("recoveringOnServer");
  const failoverActive = waitingFailover || recoveringOnServer;
  const items = [...thread.turns]
    .sort((left, right) => (left.startedAt ?? 0) - (right.startedAt ?? 0))
    .flatMap((turn) => turn.items.map((item) => ({ ...item, turnId: turn.id })));

  useLayoutEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      const timeline = timelineRef.current;
      if (timeline) timeline.scrollTop = timeline.scrollHeight;
    });
    return () => window.cancelAnimationFrame(frame);
  }, [thread.id, items.length, thread.updatedAt]);

  useEffect(() => {
    setTargetExecutorId((current) => targets.some((executor) => executor.id === current) ? current : targets[0]?.id ?? "");
  }, [thread.executorId, executors]);

  useEffect(() => setCategoryDraft(thread.category ?? ""), [thread.category, thread.id]);

  const updateMetadata = async (metadata: { favorite?: boolean; category?: string | null }) => {
    setMetadataSaving(true);
    try {
      const result = await api<{ thread: CodexThread }>(`/api/threads/${encodeURIComponent(thread.id)}/metadata`, {
        method: "PATCH",
        body: JSON.stringify(metadata),
      });
      onMetadataChanged(result.thread);
    } catch (caught) {
      setCategoryDraft(thread.category ?? "");
      onError(caught instanceof Error ? caught.message : "更新项目分类失败");
    } finally {
      setMetadataSaving(false);
    }
  };

  const saveCategory = () => {
    const category = categoryDraft.trim().slice(0, 80);
    if (category === (thread.category ?? "")) return;
    void updateMetadata({ category: category || null });
  };

  const updateStage = async (stage: NonNullable<CodexThread["workflow"]>["stage"]) => {
    if (!thread.workflow) return;
    try {
      await api(`/api/workflows/${encodeURIComponent(thread.workflow.id)}/tasks/${encodeURIComponent(thread.id)}/stage`, {
        method: "PATCH",
        body: JSON.stringify({ stage }),
      });
      onWorkflowChanged();
      onRefresh();
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "更新工作流阶段失败");
    }
  };

  const send = async (event: FormEvent) => {
    event.preventDefault();
    if (!message.trim()) return;
    setSending(true);
    try {
      const result = await api<{ threadId: string; forked: boolean }>(`/api/threads/${encodeURIComponent(thread.id)}/messages`, { method: "POST", body: JSON.stringify({ text: message }) });
      setMessage("");
      if (result.forked && result.threadId !== thread.id) onThreadChanged(result.threadId);
      else onRefresh();
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "发送失败");
    } finally {
      setSending(false);
    }
  };

  const interrupt = async () => {
    if (!activeTurn) return;
    try {
      await api(`/api/threads/${encodeURIComponent(thread.id)}/interrupt`, { method: "POST", body: JSON.stringify({ turnId: activeTurn.id }) });
      onRefresh();
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "中止失败");
    }
  };

  const cancelQueue = async () => {
    try {
      await api(`/api/threads/${encodeURIComponent(thread.id)}/queue`, { method: "DELETE" });
      onRefresh();
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "取消排队失败");
    }
  };

  const transfer = async () => {
    const target = targets.find((executor) => executor.id === targetExecutorId);
    if (!target) return;
    setTransferring(true);
    try {
      const result = await api<{ task: { id: string } }>(`/api/threads/${encodeURIComponent(thread.id)}/transfer`, {
        method: "POST",
        body: JSON.stringify({ executorId: target.id }),
      });
      onTransferred(result.task.id, target.name);
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "任务交接失败");
    } finally {
      setTransferring(false);
    }
  };

  const resolveConflict = async (resolution: "keep-current" | "adopt-diverged") => {
    setResolvingConflict(true);
    try {
      await api(`/api/threads/${encodeURIComponent(thread.id)}/conflict`, {
        method: "POST",
        body: JSON.stringify({ resolution }),
      });
      onRefresh();
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "冲突处理失败");
    } finally {
      setResolvingConflict(false);
    }
  };

  return (
    <div className="thread-detail">
      <header className="detail-header">
        <button className="icon-button mobile-back" title="返回" onClick={onBack}><ArrowLeft size={20} /></button>
        <div className="detail-title"><h2>{threadTitle(thread)}</h2><span><FolderGit2 size={14} />{thread.cwd}{thread.executorName && <em className="executor-label"><MonitorCog size={12} />{thread.executorName}</em>}{thread.checkpoint && <em className="checkpoint-label"><RefreshCw size={12} />检查点 {relativeTime(thread.checkpoint.createdAt)}</em>}</span></div>
        <div className="detail-actions">
          <label className="category-editor" title="项目分类">
            <Tag size={14} />
            <input
              aria-label="项目分类"
              list={`task-categories-${thread.id}`}
              maxLength={80}
              placeholder="未分类"
              value={categoryDraft}
              disabled={metadataSaving}
              onChange={(event) => setCategoryDraft(event.target.value)}
              onBlur={saveCategory}
              onKeyDown={(event) => {
                if (event.key === "Enter") event.currentTarget.blur();
                if (event.key === "Escape") {
                  setCategoryDraft(thread.category ?? "");
                  event.currentTarget.blur();
                }
              }}
            />
            <datalist id={`task-categories-${thread.id}`}>{categories.map((category) => <option key={category} value={category} />)}</datalist>
          </label>
          <button className={`icon-button favorite-button ${thread.favorite ? "active" : ""}`} title={thread.favorite ? "取消收藏" : "收藏项目"} disabled={metadataSaving} onClick={() => void updateMetadata({ favorite: !thread.favorite })}>
            {metadataSaving ? <LoaderCircle size={18} className="spin" /> : <Star size={18} fill={thread.favorite ? "currentColor" : "none"} />}
          </button>
          {targets.length > 0 && <div className="transfer-control">
            <select aria-label="目标执行器" value={targetExecutorId} onChange={(event) => setTargetExecutorId(event.target.value)} disabled={transferring || Boolean(activeTurn) || queued || failoverActive}>
              {targets.map((executor) => <option key={executor.id} value={executor.id}>{executor.name}</option>)}
            </select>
            <button className="icon-button" title={activeTurn || queued || failoverActive ? "任务运行、排队或接管中，完成后可交接" : "交接到目标执行器"} disabled={transferring || Boolean(activeTurn) || queued || failoverActive} onClick={() => void transfer()}>{transferring ? <LoaderCircle size={18} className="spin" /> : <ArrowRightLeft size={18} />}</button>
          </div>}
          {activeTurn && <button className="icon-button danger" title="中止任务" onClick={() => void interrupt()}><CircleStop size={19} /></button>}
          <button className="icon-button" title="刷新线程" onClick={onRefresh}><RefreshCw size={18} /></button>
        </div>
      </header>
      {thread.conflict && (
        <div className="conflict-banner">
          <AlertTriangle size={18} />
          <span>检测到旧任务段在 {thread.conflict.executorId} 继续写入</span>
          <button className="secondary-button" disabled={resolvingConflict} onClick={() => void resolveConflict("keep-current")}>保留当前</button>
          <button className="primary-button" disabled={resolvingConflict} onClick={() => void resolveConflict("adopt-diverged")}>切换到分歧段</button>
        </div>
      )}
      {queued && (
        <div className="queue-banner">
          <LoaderCircle size={18} className="spin" />
          <span>任务正在全局队列中等待{thread.queuePosition ? `，当前位置 ${thread.queuePosition}` : ""}</span>
          <button className="secondary-button" onClick={() => void cancelQueue()}>取消排队</button>
        </div>
      )}
      {waitingFailover && (
        <div className="failover-banner waiting">
          <LoaderCircle size={18} className="spin" />
          <span>{thread.failover?.state === "failed" ? `服务器接管失败，正在自动重试${thread.failover.lastError ? `：${thread.failover.lastError}` : ""}` : "Mac 已离线，正在等待自动故障转移"}</span>
        </div>
      )}
      {recoveringOnServer && (
        <div className="failover-banner recovering">
          <Server size={18} />
          <span>正在服务器恢复最近会话与工作区检查点，恢复后会自动继续执行</span>
        </div>
      )}
      <div className="timeline" ref={timelineRef}>
        {thread.workflow?.role === "controller" && (
          <WorkflowPanel
            workflow={thread.workflow}
            tasks={workflowTasks}
            onCreateModule={() => setShowModuleDialog(true)}
            onStageChange={(stage) => void updateStage(stage)}
            onSelectThread={onSelectThread}
          />
        )}
        {items.map((item, index) => <TimelineItem key={`${item.id ?? item.type}-${index}`} item={item} />)}
        {items.length === 0 && <div className="empty-detail"><MessageSquareText size={28} /><span>线程暂无消息</span></div>}
      </div>
      <form className="composer" onSubmit={send}>
        <textarea aria-label="继续任务" placeholder={failoverActive ? "任务正在服务器接管" : queued ? "任务排队中" : "继续任务…"} disabled={queued || failoverActive} rows={1} value={message} onChange={(event) => setMessage(event.target.value)} onKeyDown={(event) => {
          if (event.key === "Enter" && !event.shiftKey) { event.preventDefault(); event.currentTarget.form?.requestSubmit(); }
        }} />
        <button className="send-button" title="发送" disabled={sending || queued || failoverActive || Boolean(thread.conflict) || !message.trim()}>{sending ? <LoaderCircle size={19} className="spin" /> : <Send size={19} />}</button>
      </form>
      {showModuleDialog && thread.workflow && (
        <ModuleDialog
          workflowId={thread.workflow.id}
          parentTaskId={thread.id}
          onClose={() => setShowModuleDialog(false)}
          onCreated={() => {
            setShowModuleDialog(false);
            onWorkflowChanged();
          }}
          onError={onError}
        />
      )}
    </div>
  );
}

function WorkflowPanel({ workflow, tasks, onCreateModule, onStageChange, onSelectThread }: { workflow: NonNullable<CodexThread["workflow"]>; tasks: CodexThread[]; onCreateModule: () => void; onStageChange: (stage: NonNullable<CodexThread["workflow"]>["stage"]) => void; onSelectThread: (threadId: string) => void }) {
  const modules = tasks.filter((task) => task.workflow?.role === "module");
  return (
    <section className="workflow-panel">
      <div className="workflow-heading">
        <div><ListTree size={17} /><strong>开发主控工作流</strong><span>{modules.length} 个模块</span></div>
        <button className="secondary-button compact-action" onClick={onCreateModule}><Plus size={15} />创建模块</button>
      </div>
      <div className="workflow-controls">
        <span>主控阶段</span>
        <select aria-label="工作流阶段" value={workflow.stage} onChange={(event) => onStageChange(event.target.value as NonNullable<CodexThread["workflow"]>["stage"])}>
          {(["intake", "clarifying", "planning", "execution", "integration", "review", "delivered", "blocked"] as const).map((stage) => <option key={stage} value={stage}>{workflowStageLabel(stage)}</option>)}
        </select>
      </div>
      <p className="workflow-hint">主控先澄清需求，确认后拆分模块；模块完成后由主控统一集成、审批和交付。</p>
      <div className="workflow-modules">
        {modules.map((module) => (
          <button className="workflow-module" key={module.id} onClick={() => onSelectThread(module.id)}>
            <span className={`status-dot ${statusKind(module.status)}`} />
            <span className="workflow-module-copy"><strong>{moduleTitle(module)}</strong><small>{module.category || shortPath(module.cwd)}</small></span>
            <StatusText kind={statusKind(module.status)} label={statusLabel(module.status)} />
            <ChevronRight size={16} />
          </button>
        ))}
        {modules.length === 0 && <div className="workflow-empty">确认需求后，在这里创建独立模块任务。</div>}
      </div>
    </section>
  );
}

function ModuleDialog({ workflowId, parentTaskId, onClose, onCreated, onError }: { workflowId: string; parentTaskId: string; onClose: () => void; onCreated: () => void; onError: (value: string) => void }) {
  const [title, setTitle] = useState("");
  const [goal, setGoal] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!title.trim() || !goal.trim()) return;
    setSubmitting(true);
    try {
      await api(`/api/workflows/${encodeURIComponent(workflowId)}/modules`, {
        method: "POST",
        body: JSON.stringify({ parentTaskId, title, goal }),
      });
      onCreated();
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "创建模块失败");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <form className="modal task-modal" role="dialog" aria-modal="true" onSubmit={submit}>
        <header className="modal-header"><div><span className="modal-icon"><ListTree size={19} /></span><h2>创建模块任务</h2></div><button type="button" className="icon-button" title="关闭" onClick={onClose}><X size={19} /></button></header>
        <div className="task-form">
          <label><span>模块名称</span><input value={title} onChange={(event) => setTitle(event.target.value)} placeholder="例如：图像管线" autoFocus /></label>
          <label><span>模块目标</span><textarea rows={6} value={goal} onChange={(event) => setGoal(event.target.value)} placeholder="描述模块范围、输入、输出和验收标准" /></label>
        </div>
        <footer className="modal-actions"><button type="button" className="secondary-button" onClick={onClose}>取消</button><button className="primary-button" disabled={submitting || !title.trim() || !goal.trim()}>{submitting ? <LoaderCircle size={17} className="spin" /> : <Plus size={17} />}创建并运行</button></footer>
      </form>
    </div>
  );
}

function TimelineItem({ item }: { item: ThreadItem }) {
  const text = itemText(item);
  if (item.type === "userMessage" || item.type === "agentMessage" || item.type === "plan" || item.type === "reasoning") {
    return (
      <article className={`message-row ${item.type}`}>
        <div className="message-label">{item.type === "userMessage" ? "你" : item.type === "agentMessage" ? "Codex" : item.type === "plan" ? "计划" : "推理"}</div>
        <div className="message-text markdown-body"><ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown></div>
      </article>
    );
  }
  if (item.type === "commandExecution") {
    return (
      <article className="activity-row">
        <div className="activity-heading"><TerminalSquare size={16} /><strong>命令</strong><span className={`activity-status ${item.status}`}>{item.status}</span></div>
        <code>{item.command}</code>
        {item.aggregatedOutput && <pre>{item.aggregatedOutput}</pre>}
      </article>
    );
  }
  if (item.type === "fileChange") {
    return <article className="activity-row compact-activity"><FileCode2 size={16} /><strong>文件变更</strong><span>{item.changes?.length ?? 0} 项</span></article>;
  }
  if (item.type === "mcpToolCall" || item.type === "dynamicToolCall") {
    return <article className="activity-row compact-activity"><Code2 size={16} /><strong>{item.tool || "工具调用"}</strong><span>{item.status}</span></article>;
  }
  return null;
}

function ApprovalDialog({ approval, onClose, onResolved, onError }: { approval: Approval; onClose: () => void; onResolved: () => void; onError: (value: string) => void }) {
  const [submitting, setSubmitting] = useState(false);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const params = approval.params;
  const questions = Array.isArray(params.questions) ? params.questions as Array<{ id?: string; header?: string; question?: string }> : [];
  const command = typeof params.command === "string" ? params.command : null;

  const decide = async (decision: "accept" | "acceptForSession" | "decline" | "cancel") => {
    setSubmitting(true);
    try {
      await api(`/api/approvals/${encodeURIComponent(approval.key)}`, {
        method: "POST",
        body: JSON.stringify({
          decision,
          answers: Object.fromEntries(Object.entries(answers).map(([key, value]) => [key, { answers: [value] }])),
        }),
      });
      onResolved();
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "审批失败");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="modal approval-modal" role="dialog" aria-modal="true" aria-labelledby="approval-title">
        <header className="modal-header"><div><span className="modal-icon warning"><ShieldCheck size={19} /></span><h2 id="approval-title">等待审批</h2></div><button className="icon-button" title="关闭" onClick={onClose}><X size={19} /></button></header>
        <div className="approval-content">
          <div className="approval-type">{approvalLabel(approval.method)}</div>
          {command && <pre className="approval-command">{command}</pre>}
          {typeof params.cwd === "string" && <InfoRow label="目录" value={params.cwd} />}
          {typeof params.reason === "string" && <InfoRow label="原因" value={params.reason} />}
          {params.permissions != null && <JsonBlock label="权限" value={params.permissions} />}
          {params.changes != null && <JsonBlock label="变更" value={params.changes} />}
          {questions.map((question, index) => {
            const key = question.id || String(index);
            return <label className="question-field" key={key}><span>{question.header || question.question || "回答"}</span><input value={answers[key] ?? ""} onChange={(event) => setAnswers((current) => ({ ...current, [key]: event.target.value }))} /></label>;
          })}
        </div>
        <footer className="modal-actions">
          <button className="secondary-button danger-text" disabled={submitting} onClick={() => void decide("decline")}><X size={17} />拒绝</button>
          {questions.length === 0 && <button className="secondary-button" disabled={submitting} onClick={() => void decide("acceptForSession")}><ShieldCheck size={17} />当前任务</button>}
          <button className="primary-button" disabled={submitting || questions.some((question, index) => !answers[question.id || String(index)]?.trim())} onClick={() => void decide("accept")}>
            {submitting ? <LoaderCircle size={17} className="spin" /> : <Check size={17} />}{questions.length ? "提交" : "允许"}
          </button>
        </footer>
      </section>
    </div>
  );
}

function NewTaskDialog({ initialCwd, executors, onClose, onCreated, onError }: { initialCwd: string; executors: Executor[]; onClose: () => void; onCreated: (threadId: string) => void; onError: (value: string) => void }) {
  const [cwd, setCwd] = useState(initialCwd);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [workflowMode, setWorkflowMode] = useState(false);
  const [failoverEnabled, setFailoverEnabled] = useState(true);
  const availableExecutors = executors.filter((executor) => executor.status === "connected");
  const [executorId, setExecutorId] = useState(
    availableExecutors.find((executor) => executor.kind === "server")?.id ?? availableExecutors[0]?.id ?? "",
  );

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      const selectedExecutor = availableExecutors.find((executor) => executor.id === executorId);
      const payload = await api<{ thread: CodexThread }>(workflowMode ? "/api/workflows/development" : "/api/tasks", { method: "POST", body: JSON.stringify({
        cwd,
        text,
        executorId: executorId || undefined,
        executionPolicy: selectedExecutor?.kind === "mac" ? "mac-first" : "server-first",
        failoverEnabled,
        checkpointIntervalMs: 30_000,
        maxRetries: 0,
      }) });
      onCreated(payload.thread.id);
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "创建失败");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <form className="modal task-modal" role="dialog" aria-modal="true" onSubmit={submit}>
        <header className="modal-header"><div><span className="modal-icon"><Plus size={19} /></span><h2>新建任务</h2></div><button type="button" className="icon-button" title="关闭" onClick={onClose}><X size={19} /></button></header>
        <div className="task-form">
          <label><span>工作目录</span><div className="field-with-icon"><FolderGit2 size={17} /><input value={cwd} onChange={(event) => setCwd(event.target.value)} placeholder="/Users/name/project" /></div></label>
          <label><span>执行器</span><div className="field-with-icon"><Server size={17} /><select value={executorId} onChange={(event) => setExecutorId(event.target.value)}>{availableExecutors.map((executor) => <option key={executor.id} value={executor.id}>{executor.name}</option>)}</select></div></label>
          <label className="workflow-toggle"><input type="checkbox" checked={workflowMode} onChange={(event) => setWorkflowMode(event.target.checked)} /><span><strong>开发主控工作流</strong><small>先澄清需求，再拆分模块任务，由主控统一监督和交付</small></span></label>
          <label className="workflow-toggle"><input type="checkbox" checked={failoverEnabled} onChange={(event) => setFailoverEnabled(event.target.checked)} /><span><strong>自动故障转移</strong><small>Mac 离线后由服务器从最近检查点继续</small></span></label>
          <label><span>任务内容</span><textarea rows={7} value={text} onChange={(event) => setText(event.target.value)} autoFocus /></label>
        </div>
        <footer className="modal-actions"><button type="button" className="secondary-button" onClick={onClose}>取消</button><button className="primary-button" disabled={submitting || !cwd.startsWith("/") || !text.trim()}>{submitting ? <LoaderCircle size={17} className="spin" /> : workflowMode ? <ListTree size={17} /> : <Send size={17} />}{workflowMode ? "启动主控" : "开始"}</button></footer>
      </form>
    </div>
  );
}

function ConnectionBadge({ state, executors }: { state: SessionState["codex"]; executors: Executor[] }) {
  const remoteMacConnected = executors.some((executor) => executor.kind === "mac" && executor.status === "connected");
  const connected = state === "connected" || remoteMacConnected;
  const connecting = !connected && state === "connecting";
  return <span className={`connection-badge ${connected ? "online" : "offline"}`}>{connected ? <MonitorCog size={14} /> : <Server size={14} />}{connected ? "Mac 在线" : connecting ? "连接中" : "Mac 离线"}</span>;
}

function StatusText({ kind, label }: { kind: StatusKind; label: string }) {
  return <span className={`status-text ${kind}`}>{label}</span>;
}

function FullScreenLoader({ compact = false }: { compact?: boolean }) {
  return <div className={compact ? "inline-loader" : "full-loader"}><LoaderCircle className="spin" size={25} /></div>;
}

function NoSelection({ onNew }: { onNew: () => void }) {
  return <div className="empty-detail"><TerminalSquare size={32} /><span>选择一个任务</span><button className="secondary-button" onClick={onNew}><Plus size={17} />新建任务</button></div>;
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return <div className="info-row"><span>{label}</span><strong>{value}</strong></div>;
}

function JsonBlock({ label, value }: { label: string; value: unknown }) {
  return <div className="json-block"><span>{label}</span><pre>{JSON.stringify(value, null, 2)}</pre></div>;
}

function filterLabel(filter: Filter): string {
  return { all: "全部", favorites: "我的收藏", waiting: "待处理", running: "运行中", done: "已结束" }[filter];
}

function moduleTitle(thread: CodexThread): string {
  const title = threadTitle(thread);
  return title.replace(/^\[模块\]\s*/i, "");
}

function workflowStageLabel(stage: NonNullable<CodexThread["workflow"]>["stage"]): string {
  return {
    intake: "需求入口",
    clarifying: "澄清需求",
    planning: "拆分计划",
    execution: "模块执行",
    integration: "集成联调",
    review: "验收审查",
    delivered: "已交付",
    blocked: "已阻塞",
  }[stage];
}

function approvalLabel(method: string): string {
  if (method.includes("commandExecution") || method === "execCommandApproval") return "执行命令";
  if (method.includes("fileChange") || method === "applyPatchApproval") return "修改文件";
  if (method.includes("permissions")) return "扩展权限";
  if (method.includes("requestUserInput")) return "需要输入";
  return method;
}

function showNotification(title: string, body: string) {
  if ("Notification" in window && Notification.permission === "granted") new Notification(title, { body, icon: "/console-icon.svg" });
}

async function logout(setSession: (session: SessionState) => void) {
  await api("/api/logout", { method: "POST" });
  setSession({ authenticated: false });
}

async function enablePushNotifications(): Promise<void> {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    throw new Error("当前浏览器不支持手机推送");
  }
  if (!window.isSecureContext) throw new Error("手机推送需要 HTTPS 访问控制台");
  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new Error("通知权限未开启");

  const registration = await navigator.serviceWorker.register("/sw.js");
  await navigator.serviceWorker.ready;
  const { publicKey } = await api<{ publicKey: string }>("/api/push/public-key");
  const existing = await registration.pushManager.getSubscription();
  const subscription = existing ?? await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: base64UrlToBytes(publicKey),
  });
  await api("/api/push/subscriptions", {
    method: "POST",
    body: JSON.stringify({ subscription: subscription.toJSON() }),
  });
}

function base64UrlToBytes(value: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - value.length % 4) % 4);
  const decoded = atob((value + padding).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(decoded, (character) => character.charCodeAt(0));
}
